import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';

// Create a separate instance for auth operations like token refresh
export const authAxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh token requests
let isRefreshing = false;

let failedQueue: { resolve: (value: unknown) => void; reject: (reason?: any) => void; }[] = [];

// Helper function to check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    const currentTime = Date.now() / 1000;
    return decoded.exp ? decoded.exp < currentTime : true;
  } catch (error) {
    return true;
  }
};

// Process failed requests after token refresh
const processQueue = (error: Error | null, token: string | null = null): void => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Function to refresh token
const refreshAccessToken = async (): Promise<string | null> => {
  try {
    // For now just return the existing token and log a message
    console.log('Token refresh would happen here in a real implementation');
    return localStorage.getItem('token');
  } catch (error) {
    console.error('Error refreshing token:', error);
    return null;
  }
};

// Request interceptor for adding auth tokens
axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    
    const isPublicEndpoint = config.url?.includes('/auth/login');
    
    if (token && !isPublicEndpoint) {
      // Check if token is expired
      if (isTokenExpired(token) && !isRefreshing) {
        isRefreshing = true;
        
        try {
          const newToken = await refreshAccessToken();
          
          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
            processQueue(null, newToken);
          } else {
            processQueue(new Error('Failed to refresh token'));
            localStorage.removeItem('token');
            localStorage.removeItem('username');
            localStorage.removeItem('firstName');
            localStorage.removeItem('lastName');
            
            const currentPath = window.location.pathname;
            if (currentPath !== '/login' && currentPath !== '/register') {
              window.location.href = '/login';
            }
          }
        } catch (error) {
          processQueue(error as Error);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Token is valid
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling common errors
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    
    const isPublicEndpoint = originalRequest?.url?.includes('/auth/login');
    
    if (error.response?.status === 401 && !originalRequest._retry && !isPublicEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch(err => {
              throw err;
          });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        const newToken = await refreshAccessToken();
        
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          return axiosInstance(originalRequest);
        } else {
          processQueue(new Error('Failed to refresh token'));
          // Handle failed refresh
          localStorage.removeItem('token');
          localStorage.removeItem('username');
          localStorage.removeItem('firstName');
          localStorage.removeItem('lastName');
          
          const currentPath = window.location.pathname;
          if (currentPath !== '/login' && currentPath !== '/register') {
            window.location.href = '/login';
          }
          
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError as Error);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    // For 401 errors on auth endpoints or other errors
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('firstName');
      localStorage.removeItem('lastName');

      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/register') {
        window.location.href = '/login';
      }
    }

      throw error;
  }
);

export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
  return axiosInstance(config).then((response) => response.data);
};
